import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchCategories,
  createCategory,
  updateCategory,
  updateCategoryStatus,
} from '../services/categoryService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
} from 'lucide-react';

export default function ShopCategories() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', status: '1' });
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  const loadCategories = async () => {
    try {
      const res = await fetchCategories({ search, sortBy, sortOrder });
      const records = res.data?.data?.records || res.data?.data || [];
      setCategories(records);
    } catch (err) {
      toast.error('Failed to load categories');
    }
  };

  useEffect(() => {
    loadCategories();
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (search === '') loadCategories();
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, status: Number(form.status) };
    try {
      if (editingId) {
        await updateCategory(editingId, payload);
        toast.success('Category updated');
      } else {
        await createCategory(payload);
        toast.success('Category created');
      }
      closeForm();
      loadCategories();
    } catch (err) {
      toast.error('Failed to save category');
    }
  };

  const openEdit = (cat) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, status: String(cat.status) });
    setOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm({ name: '', status: '1' });
    setOpen(false);
  };

  const toggleStatus = async (cat) => {
    const newStatus = cat.status === 1 ? 2 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this category?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateCategoryStatus(cat.id, newStatus);
      toast.success('Status updated');
      loadCategories();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadCategories();
          }}
          className="relative"
        >
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadCategories();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>

        <button
          onClick={() => setOpen(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded"
        >
          Add Category
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  {sortBy === 'name' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Created At</span>
                  {sortBy === 'createdAt' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">{cat.name}</td>
                <td className="py-2 px-3">{new Date(cat.createdAt).toLocaleDateString()}</td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${cat.status === 1 ? 'bg-green-500' : 'bg-red-500'}`}
                    onClick={() => toggleStatus(cat)}
                    title={`${cat.status === 1 ? 'Deactivate' : 'Activate'} category`}
                  >
                    {cat.status === 1 ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2">
                  <button
                    className="text-indigo-600"
                    onClick={() => openEdit(cat)}
                  >
                    <Edit size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr>
                <td className="py-2 px-3 text-center" colSpan="4">
                  <p className="text-sm text-gray-500 dark:text-gray-300">No categories</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">{editingId ? 'Edit Category' : 'Add Category'}</h2>
              <button onClick={closeForm} className="text-gray-600 dark:text-gray-300">×</button>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <input
                name="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <select
                name="status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
              <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded">
                {editingId ? 'Update' : 'Create'} Category
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

